import { atom } from "recoil";
import { selectorFamily } from "./recoilSelectors";
import { Uid } from "../types/core";

export type DayMapStop = { key: string; letter: string; lat: number; lng: number };
/** a dashed line: straight from → to, or along `path` (a travel's road) when known */
export type DayMapGap = { key: string; from: [number, number]; to: [number, number]; path?: [number, number][] };
export type DayMapDrive = { key: string; path: readonly (readonly number[])[] };

/**
 * What the map draws for the Day view: one letter per stop instead of the per-trip letters, the
 * dashed stretches without location, and the drive under the pointer. Set while the Day view is open.
 */
export type DayMap = {
    uid: Uid;
    stops: DayMapStop[];
    gaps: DayMapGap[];
    drives: DayMapDrive[];
};

export const dayMapState = atom<DayMap | undefined>({
    key: "location.day.map",
    default: undefined,
});

/** The stop or drive under the pointer, in the list or on the map. */
export const dayHoverKeyState = atom<string | undefined>({
    key: "location.day.hover",
    default: undefined,
});

/** The stop or drive picked in the list or on the map; the list opens its detail. */
export const dayActiveKeyState = atom<string | undefined>({
    key: "location.day.active",
    default: undefined,
});

/** The moment picked on the day strip, with where the person was then. */
export type DayScrub = { ts: number; lat: number; lng: number; label: string; unknown: boolean };
export const dayScrubState = atom<DayScrub | undefined>({
    key: "location.day.scrub",
    default: undefined,
});

/**
 * The field view on the map: everyone's day as thin grey lines. Set while the field view is open, and
 * only when the people change, since the map rebuilds its track geometry from it.
 */
export type FieldMap = { uids: Uid[] };
export const fieldMapState = atom<FieldMap | undefined>({
    key: "location.field.map",
    default: undefined,
});

/**
 * The person under the pointer in the field, drawn in blue on the map. Kept apart from fieldMapState so
 * a hover only recolours lines instead of rebuilding them.
 */
export const fieldHoverUidState = atom<Uid | undefined>({
    key: "location.field.hover",
    default: undefined,
});

/** Whether this person's row is the hovered one, so a hover re-renders only the two rows it touches. */
export const isFieldHoveredState = selectorFamily<boolean, Uid>({
    key: "location.field.hover.is",
    get: (uid) => ({ get }) => get(fieldHoverUidState) === uid,
});
